'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface StudentFeature {
    icon: string;
    title: string;
    description: string;
    benefits: string[];
}

const ForStudentsSection = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById('for-students-section');
        if (element) observer.observe(element);

        return () => observer.disconnect();
    }, []);

    const studentFeatures: StudentFeature[] = [
        {
            icon: "🎯",
            title: "Discover Career Roles",
            description: "Explore diverse career paths aligned with your strengths and interests through AI-powered assessments.",
            benefits: [
                "Personalized career recommendations",
                "Industry-specific skill requirements",
                "Real-world job market insights",
                "Success probability analysis"
            ]
        },
        {
            icon: "🔍",
            title: "Diagnose Skill Gaps",
            description: "Identify exactly what skills you need to develop with comprehensive assessment tools and analytics.",
            benefits: [
                "Detailed skill gap analysis",
                "Competency mapping",
                "Progress tracking dashboards",
                "Personalized learning recommendations"
            ]
        },
        {
            icon: "📈",
            title: "Build Placement Readiness",
            description: "Get placement-ready with targeted learning paths, mock interviews, and industry-standard assessments.",
            benefits: [
                "Mock interview simulations",
                "Resume building tools",
                "Placement preparation resources",
                "Industry mentor connections"
            ]
        }
    ];

    return (
        <section
            id="for-students-section"
            className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden"
        >
            {/* Background Effects */}
            <div className="absolute inset-0 bg-gradient-to-b from-background via-[#0A0A12] to-background">
                <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2
                        className={`text-4xl lg:text-5xl font-bold text-text-primary mb-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                    >
                        For <span className="gradient-text">Students</span>
                    </h2>
                    <p
                        className={`text-xl text-text-secondary max-w-3xl mx-auto transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                    >
                        Transform your potential into career success with personalized guidance and AI-powered insights
                    </p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {studentFeatures.map((feature, index) => (
                        <div
                            key={index}
                            className={`glass glass-border rounded-xl p-8 hover-lift transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                                }`}
                            style={{ transitionDelay: `${200 + index * 100}ms` }}
                        >
                            <div className="text-5xl mb-4">{feature.icon}</div>
                            <h3 className="text-2xl font-bold text-text-primary mb-3">
                                {feature.title}
                            </h3>
                            <p className="text-text-secondary mb-6">
                                {feature.description}
                            </p>
                            <ul className="space-y-2">
                                {feature.benefits.map((benefit, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-text-tertiary">
                                        <span className="text-success mt-1">✓</span>
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>


                {/* <div
                    className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}
                >
                    <Link
                        href="/for-students"
                        className="inline-block px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-md hover-lift focus-ring"
                    >
                        Explore Student Features
                    </Link>
                </div> */}
            </div>
        </section>
    );
};

export default ForStudentsSection;